import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import type { Title } from '@reader-tracker/shared/lib/database.types'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

type TitleType = Title['type']
type TitleStatus = Title['status']

interface FormState {
  canonical_name: string
  type: TitleType
  cover_url: string
  description: string
  status: TitleStatus
}

const EMPTY_FORM: FormState = {
  canonical_name: '',
  type: 'manga',
  cover_url: '',
  description: '',
  status: 'ongoing',
}

export default function MangaFormPage() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [isLoading, setIsLoading] = useState(isEdit)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    const resolvedId = id
    async function load() {
      const { data } = await supabase
        .from('titles')
        .select('*')
        .eq('id', resolvedId)
        .single()
      if (data) {
        setForm({
          canonical_name: data.canonical_name,
          type: data.type,
          cover_url: data.cover_url ?? '',
          description: data.description ?? '',
          status: data.status,
        })
      }
      setIsLoading(false)
    }
    load()
  }, [id])

  function handleChange(
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setIsSaving(true)

    const payload = {
      canonical_name: form.canonical_name.trim(),
      type: form.type,
      cover_url: form.cover_url.trim() || null,
      description: form.description.trim() || null,
      status: form.status,
    }

    if (!payload.canonical_name) {
      setError('Title name is required.')
      setIsSaving(false)
      return
    }

    if (isEdit && id) {
      const { error: err } = await supabase
        .from('titles')
        .update(payload)
        .eq('id', id)
      if (err) {
        setError(err.message)
        setIsSaving(false)
        return
      }
    } else {
      const { error: err } = await supabase.from('titles').insert(payload)
      if (err) {
        setError(err.message)
        setIsSaving(false)
        return
      }
    }

    navigate('/admin/manga')
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-7 w-7 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-xl">
      <button
        type="button"
        onClick={() => navigate('/admin/manga')}
        className="mb-4 inline-flex items-center gap-1 text-gray-500 text-sm hover:text-gray-800"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to list
      </button>

      <h1 className="mb-6 font-bold text-gray-900 text-xl">
        {isEdit ? 'Edit title' : 'Add title'}
      </h1>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <p className="rounded-lg bg-red-50 px-4 py-2 text-red-600 text-sm">
            {error}
          </p>
        )}

        <div>
          <label
            htmlFor="canonical_name"
            className="mb-1 block font-medium text-gray-700 text-sm"
          >
            Title name <span className="text-red-500">*</span>
          </label>
          <input
            id="canonical_name"
            name="canonical_name"
            type="text"
            required
            value={form.canonical_name}
            onChange={handleChange}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="type"
              className="mb-1 block font-medium text-gray-700 text-sm"
            >
              Type
            </label>
            <select
              id="type"
              name="type"
              value={form.type}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="manga">Manga</option>
              <option value="manhwa">Manhwa</option>
              <option value="manhua">Manhua</option>
              <option value="webtoon">Webtoon</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="status"
              className="mb-1 block font-medium text-gray-700 text-sm"
            >
              Status
            </label>
            <select
              id="status"
              name="status"
              value={form.status}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="hiatus">Hiatus</option>
            </select>
          </div>
        </div>

        <div>
          <label
            htmlFor="cover_url"
            className="mb-1 block font-medium text-gray-700 text-sm"
          >
            Cover image URL
          </label>
          <input
            id="cover_url"
            name="cover_url"
            type="url"
            value={form.cover_url}
            onChange={handleChange}
            placeholder="https://..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="description"
            className="mb-1 block font-medium text-gray-700 text-sm"
          >
            Description
          </label>
          <textarea
            id="description"
            name="description"
            rows={4}
            value={form.description}
            onChange={handleChange}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <button
          type="submit"
          disabled={isSaving}
          className="w-full rounded-lg bg-blue-600 py-2 font-medium text-sm text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {isSaving ? 'Saving…' : isEdit ? 'Save changes' : 'Create title'}
        </button>
      </form>
    </div>
  )
}
